import React from 'react';
import { FormattedMessage } from 'react-intl';

import AutocompleteSearch from '@components/common/autocomplete-search/AutocompleteSearch';
import GIButton from '@components/common/button/GIButton';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import Multiselect from '@components/common/form/multiSelect/MultiSelect';
import Input from '@components/common/form/new/Input';
import Dropdown from '@lib/inputs/Dropdown';
import DatePicker from '@lib/inputs/react-datepicker';
import { DATE_FORMATS } from '@utils/dates/dateMethods';
import { environmentSelector, useAppSelector } from '../../../members/utils';
import {
    AUTOCOMPLETE_DEBOUNCE_DELAY,
    AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
    confidentialTicketsOptions,
    CustomFiedType,
    customFieldTypes,
    defaultOptions,
    ticketPriorityOptions,
    ticketStatusAdvanceOptions,
    watchStatusAdvancedOptions,
} from '../constants/ticketlist.constants';
import { IAdvanceSearchProps } from '../constants/ticketlist.interface';
import {
    FlexContainer,
    FlexItem,
    FlexRow,
    StyledError,
    StyledForm,
    StyledSearchControl,
    StyledSearchControlActions,
} from '../constants/ticketlist.styled';
import { handleChange } from '../constants/ticketlist.utils';
import { useAdvanceSearch } from './useAdvanceSearch';

export const AdvanceSearch = (props: IAdvanceSearchProps) => {
    const { resetTrigger, setResetTrigger, isWatchTicketAllowed } = props;
    const { user } = useAppSelector(environmentSelector) ?? {};
    const {
        handleSubmit,
        setValue,
        onSubmit,
        workgroupOptions,
        fetchAssignee,
        fetchTicketRequestors,
        roleList,
        tagsList,
        customFields,
        tkmWorkflows,
        fetchCreatedFor,
        onCancel,
        handleTicketCategoryChange,
        ticketCategory,
        customFieldOptions,
        getCustomField,
        handleCustomFieldChange,
        formState: { errors },
        isCustomTextInput,
        isCustomDateInput,
        isCustomOptionInput,
        workgroupTagListOptions,
        userTagListOptions,
        values,
        setSelectedItem,
        highlightedFields,
        handleWorkgroupChange,
        handleCustomFieldValueChange,
        loadTagsIfNeeded,
    } = useAdvanceSearch({ setResetTrigger });

    return (
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <FlexContainer>
                <FlexRow>
                    <FlexItem highlight={highlightedFields?.ticketSubject}>
                        <Input
                            label={{
                                labelText: 'ticketList.label.searchSummary',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: false,
                            }}
                            input={{
                                id: 'ticketsearchSummary',
                                isInputRequired: false,
                                type: 'text',
                                value: values.ticketSubject,
                            }}
                            actions={{
                                handleFieldChange: value => handleChange('ticketSubject', value, setValue),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketComments}>
                        <Input
                            label={{
                                labelText: 'ticketList.label.comments',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: false,
                            }}
                            input={{
                                id: 'ticketComments',
                                isInputRequired: false,
                                type: 'text',
                                value: values.ticketComments,
                            }}
                            actions={{
                                handleFieldChange: value => handleChange('ticketComments', value, setValue),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.workGroupBytagId}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.workgroupByTag" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'workGroupBytagId',
                                inputClasses: [''],
                                options: [...defaultOptions, ...workgroupTagListOptions],
                                type: 'dropdown',
                                value: values.workGroupBytagId,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldFocus: loadTagsIfNeeded,
                                handleFieldChange: value => handleWorkgroupChange(value),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketQueues}>
                        <Multiselect
                            id="workgroup"
                            label="ticketList.label.workgroup"
                            value={values.ticketQueues}
                            options={workgroupOptions}
                            onChange={value => handleChange('ticketQueues', value, setValue)}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketStatus}>
                        <Multiselect
                            id="ticketStatus"
                            label="ticketList.label.ticketStatus"
                            value={values.ticketStatus}
                            options={ticketStatusAdvanceOptions}
                            onChange={value => handleChange('ticketStatus', value, setValue)}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketNumber}>
                        <Input
                            label={{
                                labelText: 'ticketList.label.ticketNumber',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: false,
                            }}
                            input={{
                                id: 'ticketNumber',
                                isInputRequired: false,
                                type: 'text',
                                value: values.ticketNumber,
                            }}
                            actions={{
                                handleFieldChange: value => handleChange('ticketNumber', value, setValue),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.submittedAfter}>
                        <DatePicker
                            id="submittedOnAfter"
                            name="submittedOnAfter"
                            labelText="ticketList.label.submittedOnAfter"
                            wrapperClasses={['date-picker']}
                            type="text"
                            pattern={DATE_FORMATS.MM_DD_YYYY}
                            value={values.submittedAfter}
                            withMask
                            onDateChange={value => handleChange('submittedAfter', value, setValue)}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.submittedBefore}>
                        <DatePicker
                            id="submittedOnBefore"
                            name="submittedOnBefore"
                            labelText="ticketList.label.submittedOnBefore"
                            wrapperClasses={['date-picker']}
                            type="text"
                            pattern={DATE_FORMATS.MM_DD_YYYY}
                            value={values.submittedBefore}
                            withMask
                            onDateChange={value => handleChange('submittedBefore', value, setValue)}
                        />
                        <StyledError>{errors?.submittedBefore?.message}</StyledError>
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.assignee}>
                        <AutocompleteSearch
                            {...{
                                id: 'assignee-component-autocomplete',
                                setSelectedItems: value => setSelectedItem(value, 'assignee', 'assigneeId'),
                                selectedItems: [{ id: values.assigneeId, label: values.assignee }],
                                isSingleSelect: true,
                                label: 'ticketList.label.assignee',
                                isClearInputValue: false,
                                fetchResults: fetchAssignee,
                                debounceSearchTime: AUTOCOMPLETE_DEBOUNCE_DELAY,
                                minSearchQueryLength: AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
                                valueKey: 'label',
                                resetTrigger,
                                isClearApiResults: true,
                                initialSearchInputValue: values.assignee,
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.tagId}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.assigneeByTag" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'tagId',
                                inputClasses: [''],
                                options: [...defaultOptions, ...userTagListOptions],
                                type: 'dropdown',
                                value: values.tagId,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldFocus: loadTagsIfNeeded,
                                handleFieldChange: value => handleChange('tagId', value, setValue),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.requestedByName}>
                        <AutocompleteSearch
                            {...{
                                id: 'requested-by-component-autocomplete',
                                setSelectedItems: value => setSelectedItem(value, 'requestedByName', 'requestedBy'),
                                selectedItems: [{ id: values.requestedBy, label: values.requestedByName }],
                                isSingleSelect: true,
                                label: 'ticketList.label.requestedBy',
                                isClearInputValue: false,
                                fetchResults: fetchTicketRequestors,
                                debounceSearchTime: AUTOCOMPLETE_DEBOUNCE_DELAY,
                                minSearchQueryLength: AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
                                valueKey: 'label',
                                resetTrigger,
                                isClearApiResults: true,
                                initialSearchInputValue: values.requestedByName,
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.requestedBytagId}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.requestedByTag" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'requestedByTag',
                                inputClasses: [''],
                                options: [...defaultOptions, ...userTagListOptions],
                                type: 'dropdown',
                                value: values.requestedBytagId,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldFocus: loadTagsIfNeeded,
                                handleFieldChange: value => handleChange('requestedBytagId', value, setValue),
                            }}
                        />
                    </FlexItem>

                    <FlexItem highlight={highlightedFields?.userRole}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.createdForRole" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'userRole',
                                inputClasses: [''],
                                options: [
                                    ...defaultOptions,
                                    ...(roleList?.map(role => ({ value: role.id, text: role.label, ...role })) || []),
                                ],
                                type: 'dropdown',
                                value: values.userRole,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldChange: value => {
                                    if (!value) {
                                        setValue('requester', '');
                                    }
                                    handleChange('userRole', value, setValue);
                                },
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.requester}>
                        <AutocompleteSearch
                            {...{
                                id: 'created-for-component-autocomplete',
                                setSelectedItems: value => setSelectedItem(value, 'requester', 'roleId'),
                                selectedItems: [{ id: values.roleId, label: values.requester }],
                                isSingleSelect: true,
                                label: 'ticketList.label.createdFor',
                                isClearInputValue: false,
                                fetchResults: fetchCreatedFor,
                                debounceSearchTime: AUTOCOMPLETE_DEBOUNCE_DELAY,
                                minSearchQueryLength: AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH,
                                valueKey: 'label',
                                resetTrigger,
                                isClearApiResults: true,
                                initialSearchInputValue: values.requester,
                                disabled: !values.userRole,
                            }}
                        />
                        <StyledError>{errors?.requester?.message}</StyledError>
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.customFieldSelect}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.customField" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'customField',
                                inputClasses: [''],
                                options: [
                                    ...defaultOptions,
                                    ...(customFields?.map(customField => ({
                                        value: customField.cfieldParamName,
                                        text: customField.cfieldName,
                                        ...customField,
                                    })) || []),
                                ],
                                type: 'dropdown',
                                value: values.customFieldSelect,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldChange: value => handleCustomFieldChange(value),
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.customFieldSelectVal}>
                        <ConditionalRendering isTrue={isCustomTextInput}>
                            <Input
                                label={{
                                    labelText: 'ticketList.label.customFieldValue',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: false,
                                }}
                                input={{
                                    id: 'customFieldValue',
                                    isInputRequired: false,
                                    type: 'text',
                                    value: values.customFieldSelectVal,
                                    disabled: !values.customFieldSelect,
                                }}
                                actions={{
                                    handleFieldChange: value => handleCustomFieldValueChange(value),
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isCustomOptionInput}>
                            <Dropdown
                                label={{
                                    text: <FormattedMessage id="ticketList.label.customFieldValue" />,
                                    labelClass: [''],
                                }}
                                inputData={{
                                    id: 'customFieldValue',
                                    inputClasses: [''],
                                    options: [
                                        ...defaultOptions,
                                        ...(customFieldOptions?.map(option => ({
                                            value: option.id,
                                            text: option.value,
                                        })) || []),
                                    ],
                                    type: 'dropdown',
                                    value: values.customFieldSelectValOptionId,
                                    isInputRequired: false,
                                }}
                                inputActions={{
                                    handleFieldChange: value => handleCustomFieldValueChange(value),
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isCustomDateInput}>
                            <DatePicker
                                id="customFieldValue"
                                labelText="ticketList.label.customFieldValue"
                                wrapperClasses={['date-picker']}
                                type="text"
                                pattern={DATE_FORMATS.MM_DD_YYYY}
                                value={values.customFieldSelectVal}
                                withMask
                                onDateChange={value => handleCustomFieldValueChange(value)}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering
                            isTrue={
                                !CustomFiedType.includes(
                                    getCustomField(values.customFieldSelect as customFieldTypes)?.cfieldType,
                                )
                            }
                        >
                            <Input
                                label={{
                                    labelText: 'ticketList.label.customFieldValue',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: false,
                                }}
                                input={{
                                    id: 'customFieldValue',
                                    isInputRequired: false,
                                    type: 'text',
                                    value: values.customFieldSelectVal,
                                    disabled: true,
                                }}
                                actions={{
                                    handleFieldChange: value => handleCustomFieldValueChange(value),
                                }}
                            />
                        </ConditionalRendering>
                    </FlexItem>

                    <FlexItem highlight={highlightedFields?.ticketCategorySelect}>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.label.ticketCategory" />,
                                labelClass: [''],
                            }}
                            inputData={{
                                id: 'ticketCategory',
                                inputClasses: [''],
                                options: [
                                    ...defaultOptions,
                                    ...(tkmWorkflows?.map(workflow => ({ value: workflow, text: workflow })) || []),
                                ],
                                type: 'dropdown',
                                value: values.ticketCategorySelect,
                                isInputRequired: false,
                            }}
                            inputActions={{
                                handleFieldChange: value => {
                                    if (!value) {
                                        setValue('ticketType', []);
                                    }
                                    handleTicketCategoryChange(value);
                                },
                            }}
                        />
                    </FlexItem>
                    <FlexItem highlight={highlightedFields?.ticketType}>
                        <Multiselect
                            id="ticketType"
                            label="ticketList.label.ticketType"
                            value={values.ticketType}
                            options={ticketCategory?.map(category => ({ key: category.type, value: category.type }))}
                            onChange={value => handleChange('ticketType', value, setValue)}
                            isDisabled={Boolean(!values.ticketCategorySelect.length)}
                        />
                    </FlexItem>
                    <FlexItem>
                        <Multiselect
                            id="priority"
                            label="ticketList.label.priority"
                            value={values.ticketPriority}
                            options={ticketPriorityOptions}
                            onChange={value => handleChange('ticketPriority', value, setValue)}
                        />
                    </FlexItem>
                    <ConditionalRendering isTrue={isWatchTicketAllowed}>
                        <FlexItem>
                            <Multiselect
                                id="watchStatusAdvanced"
                                label="ticketList.label.watcherStatus"
                                value={values.watchStatusAdvanced}
                                options={watchStatusAdvancedOptions}
                                onChange={value => handleChange('watchStatusAdvanced', value, setValue)}
                            />
                        </FlexItem>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={user?.isSahUser}>
                        <FlexItem>
                            <Multiselect
                                id="confidentialTicketsFilter"
                                label="ticket.sah.header.confidentialTickets"
                                value={values.confidentialTicketsFilter}
                                options={confidentialTicketsOptions}
                                onChange={value => handleChange('confidentialTicketsFilter', value, setValue)}
                            />
                        </FlexItem>
                    </ConditionalRendering>
                </FlexRow>
                <StyledSearchControl>
                    <StyledSearchControlActions>
                        <FlexItem>
                            <GIButton
                                id="advanceResetButton"
                                labelId="ticketList.btn.resetToDefault"
                                type="reset"
                                variant="secondary"
                                isDisabled={false}
                                onClickHandler={onCancel}
                            />
                        </FlexItem>
                        <FlexItem>
                            <GIButton
                                id="advanceSearchButton"
                                labelId="ticketList.btn.searchAllTicket"
                                type="submit"
                                isDisabled={false}
                            />
                        </FlexItem>
                    </StyledSearchControlActions>
                </StyledSearchControl>
            </FlexContainer>
        </StyledForm>
    );
};
